/**
 * CotizacionModificacionRuta.jsx
 * ─────────────────────────────────────────────────────────────
 * Orquestador del wizard. Solo responsable de:
 *   - Coordinar useCmrForm + useCmrCatalogs
 *   - Controlar el paso activo y la navegación
 *   - Renderizar el layout (header, steps, contenido, navbar)
 *   - Redirigir al editor de trazo cuando el tipo de cambio lo requiere
 *
 * Props:
 *   modoEdicion  {boolean}        – true cuando se edita una cotización existente
 *   cotizacionId {string|number}  – ID de la cotización (necesario para la URL del trazo)
 */

import { useState, useEffect, useCallback, memo } from "react";
import { useNavigate, useParams }                 from "react-router-dom";
import { Button }                                 from "primereact/button";
import { ProgressSpinner }                        from "primereact/progressspinner";

import { useCmrForm }                    from "./hooks/useCmrForm";
import { useCmrCatalogs }                from "./hooks/useCmrCatalogs";
import { CmrStepsBar, CmrInfoBanner }    from "./cmrShared";
import {hasTrazoCambio,  getIdsPorAccion} from "./hooks/ChangeTypes";
import Paso1SeleccionRuta                from "./Steps/Paso1SeleccionarRuta";
import Paso2ModificarEstructuras         from "./Steps/Paso2ModificarEstructuras";
import Paso3Confirmar                    from "./Steps/Paso3Confirmar";
import { desEncriptar }                  from "../../../utilidades/encriptacion";
import {
    createQuoteFromRoute,
    getQuoteById,
    updateQuoteFromRoute,
} from "./Integracion";

import "./cotizacionModificacionRuta.scss";
import Swal from "sweetalert2";
import {formatearFechaHora} from "../../../utilidades/formateos";

// ─── PANTALLA DE ÉXITO ────────────────────────────────────────────────────────

const SuccessScreen = memo(({ onNueva, modoEdicion }) => (
    <div className="cmr-success flex align-items-center justify-content-center">
        <div className="text-center" style={{ maxWidth: 420 }}>
            <i
                className="pi pi-check-circle mb-4"
                style={{ fontSize: "4rem", color: "var(--green-500)" }}
                aria-hidden="true"
            />
            <h2 style={{ color: "var(--green-600)" }}>
                {modoEdicion ? "¡Cotización actualizada!" : "¡Cotización guardada!"}
            </h2>
            <p className="text-color-secondary mb-4">
                {modoEdicion
                    ? "Los cambios fueron registrados exitosamente."
                    : "La cotización de modificación de ruta fue registrada exitosamente."}
            </p>
            {!modoEdicion && (
                <Button label="Nueva cotización" icon="pi pi-plus" onClick={onNueva} />
            )}
        </div>
    </div>
));
SuccessScreen.displayName = "SuccessScreen";

// ─── PANTALLA DE CARGA ────────────────────────────────────────────────────────

const LoadingScreen = () => (
    <div className="flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
        <div className="text-center">
            <ProgressSpinner style={{ width: 48, height: 48 }} />
            <p className="text-color-secondary mt-3">Cargando cotización...</p>
        </div>
    </div>
);

// ─── WIZARD ───────────────────────────────────────────────────────────────────

export default function CotizacionModificacionRuta({ modoEdicion = false, onGuardado }) {
    const navigate        = useNavigate();
    const { id }          = useParams(); // presente solo en modo edición

    const session = JSON.parse(
        desEncriptar(sessionStorage.getItem("dataClient"), process.env.REACT_APP_CLAVE_INCRIPTACION)
    );
    const userId = session?.user?.ID;

    // ── Formulario ────────────────────────────────────────────────────────────
    const { state, dispatch, isPasoValid } = useCmrForm();
    const catalogs = useCmrCatalogs(state.clienteId, state.rutaId, state.changeType);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [paso,               setPaso]               = useState(1);
    const [guardado,           setGuardado]           = useState(false);
    const [loadingCotizacion,  setLoadingCotizacion]  = useState(modoEdicion);
    const [errorCarga,         setErrorCarga]         = useState(null);

    const puedeAvanzar = isPasoValid(paso, catalogs.rutas.data);
    const tieneTrazo   = hasTrazoCambio(state.changeType);

    // ── Cargar cotización existente (solo modo edición) ───────────────────────
    useEffect(() => {
        if (!modoEdicion || !id) return;

        setLoadingCotizacion(true);
        setErrorCarga(null);

        getQuoteById(id)
            .then((res) => {
                dispatch({ type: "LOAD_COTIZACION", payload: res.data });
            })
            .catch(() => setErrorCarga("No se pudo cargar la cotización. Intenta de nuevo."))
            .finally(() => setLoadingCotizacion(false));
    }, [modoEdicion, id]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Guardar / Actualizar ──────────────────────────────────────────────────
    const handleGuardar = useCallback(async () => {
        // El backend separa base/copias con estructurasSeleccionadas + estructurasAccion,
        // así que se manda la selección completa. Solo se filtran las modificaciones
        // para no enviar ediciones de turnos que ya no se editan directo.
        const { idsAEditar } = getIdsPorAccion(state);
        const modificaciones = Object.fromEntries(
            Object.entries(state.modificaciones).filter(([estId]) => idsAEditar.includes(Number(estId)))
        );

        const data = {
            ...state,
            modificaciones,
            fechaTentativa: formatearFechaHora(state.fechaTentativa),
            usuarioId: userId,
            cotizacionId: parseInt(id, 10),
        };

        try {
            if (tieneTrazo) {
                const quote = modoEdicion
                    ? await updateQuoteFromRoute(data)
                    : await createQuoteFromRoute(data);

                const idCotizacion = quote?.data?.cotizacionId;
                onGuardado(idCotizacion);
                return;
            }

            if (modoEdicion) {
                const quote = await updateQuoteFromRoute(data);
                const idCotizacion = quote?.data?.cotizacionId;
                onGuardado(idCotizacion);

            } else {
                const quote = await createQuoteFromRoute(data);
                const idCotizacion = quote?.data?.cotizacionId;
                onGuardado(idCotizacion);

            }
            setGuardado(true);

        } catch (error) {
            await Swal.fire({
                icon: "error",
                title: "No se pudo crear la cotización",
                text: "Ocurrió un error al intentar enviar la información. Intenta nuevamente.",
                timer: 2500,
                showConfirmButton: false
            });
        }
    }, [state, tieneTrazo, modoEdicion, id, userId, navigate]);

    // ── Handlers de navegación ────────────────────────────────────────────────
    const handleSiguiente = () => {
        if (paso === 1) {
            const ruta = catalogs.rutas.data.find((r) => r.id === state.rutaId);
            dispatch({ type: "SINCRONIZAR_COPIAS", payload: ruta?.estructuras });
        }
        setPaso((p) => p + 1);
    };
    const handleAnterior  = useCallback(() => setPaso((p) => p - 1), []);

    const handleNueva = useCallback(() => {
        dispatch({ type: "RESET" });
        setGuardado(false);
        setPaso(1);
    }, [dispatch]);

    // ── Renders condicionales ─────────────────────────────────────────────────
    if (loadingCotizacion) return <LoadingScreen />;

    if (errorCarga) return (
        <div className="cmr-root">
            <CmrInfoBanner severity="error">
                <strong>Error:</strong> {errorCarga}
            </CmrInfoBanner>
        </div>
    );

    if (guardado) return <SuccessScreen onNueva={handleNueva} modoEdicion={modoEdicion} />;

    const stepProps = { state, dispatch, catalogs };

    return (
        <div className="cmr-root">

            <header className="cmr-header flex align-items-center gap-3 p-4 mb-4">
                <i className="cmr-header__icon pi pi-pencil" aria-hidden="true" />
                <div>
                    <h1 className="cmr-header__title">Cotización – Modificación de Ruta</h1>
                    <p className="cmr-header__sub">
                        Registra cambios en extensiones, reducciones, horarios o tipo de unidad
                    </p>
                </div>
            </header>

            {modoEdicion && (
                <div className="cmr-banner-edicion flex align-items-center gap-2 p-3 mb-3">
                    <i className="pi pi-pencil" aria-hidden="true" />
                    <span>
                        <strong>Modo edición</strong> — Editando cotización <strong>#{id}</strong>.
                        Los cambios se guardarán al hacer clic en "Actualizar Cotización".
                    </span>
                </div>
            )}

            <CmrStepsBar current={paso} />

            <main className="cmr-content" aria-live="polite">
                {paso === 1 && <Paso1SeleccionRuta       {...stepProps} />}
                {paso === 2 && <Paso2ModificarEstructuras {...stepProps} />}
                {paso === 3 && <Paso3Confirmar            {...stepProps} modoEdicion={modoEdicion} />}
            </main>

            <nav
                className="cmr-nav flex justify-content-between align-items-center p-3 mt-3"
                aria-label="Navegación del wizard"
            >
                <div>
                    {paso > 1 && (
                        <Button
                            label="Anterior"
                            icon="pi pi-arrow-left"
                            severity="secondary"
                            outlined
                            onClick={handleAnterior}
                        />
                    )}
                </div>

                <span className="cmr-step-indicator px-3 py-2" aria-live="polite">
                    Paso {paso} de 3
                </span>

                <div>
                    {paso < 3 && (
                        <Button
                            label="Siguiente"
                            iconPos="right"
                            icon="pi pi-arrow-right"
                            disabled={!puedeAvanzar}
                            onClick={handleSiguiente}
                        />
                    )}

                    {paso === 3 && (
                        <Button
                            label={
                                modoEdicion ? "Actualizar Cotización" : "Guardar Cotización"
                            }
                            icon={tieneTrazo ? "pi pi-map" : "pi pi-check"}
                            severity={tieneTrazo ? "warning" : "success"}
                            onClick={handleGuardar}
                        />
                    )}
                </div>
            </nav>
        </div>
    );
}
